import { isNil } from 'lodash';
import moment, { Moment } from 'moment-timezone';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { DateRangePicker } from 'sonata-react-components';

type Props = {
  id: string;
  isOutsideRange?: (day: Moment) => boolean;
  onFilterStatus?: (state: { id: string; value: any }[]) => { id: string; value: any }[];
  value?: {
    beginDate?: string;
    endDate?: string;
  };
};

type State = {
  beginDate: Moment | null;
  endDate: Moment | null;
};

const DATE_FORMAT = 'YYYY-MM-DD';

const stringsToMomentsConverter = (args?: { beginDate?: string; endDate?: string }): State => {
  const { beginDate, endDate } = args ?? {};
  return {
    beginDate: beginDate ? moment(beginDate, DATE_FORMAT) : null,
    endDate: endDate ? moment(endDate, DATE_FORMAT) : null
  };
};

const DateRange = ({ id, isOutsideRange, onFilterStatus, value }: Props) => {
  const { beginDate: initialBeginDate, endDate: initialEndDate } = stringsToMomentsConverter(value);
  const [{ beginDate, endDate }, setState] = useState<State>({
    beginDate: initialBeginDate,
    endDate: initialEndDate
  });
  const { formatMessage } = useIntl();

  const handleDatesChange = (dates: { endDate: Moment | null; startDate: Moment | null }) => {
    setState(prev => ({
      ...prev,
      beginDate: dates.startDate,
      endDate: dates.endDate
    }));
    if (Object.values(dates).every(Boolean) || Object.values(dates).every(isNil)) {
      onFilterStatus?.([
        {
          id: 'dates',
          value: {
            beginDate: dates.startDate?.format(DATE_FORMAT) ?? null,
            endDate: dates.endDate?.format(DATE_FORMAT) ?? null
          }
        }
      ]);
    }
  };

  return (
    <DateRangePicker
      displayFormat={DATE_FORMAT}
      endDate={endDate}
      endDateId={`date_range_end_date_${id}`}
      endDatePlaceholderText={formatMessage({
        defaultMessage: 'To',
        id: 'campaignPlans.filters.date.to'
      })}
      focusedInput={null}
      inputIconPosition="after"
      isOutsideRange={isOutsideRange ?? (() => false)}
      label={formatMessage({
        defaultMessage: 'Date',
        id: 'campaignPlans.filters.date.label'
      })}
      minimumNights={0}
      onDatesChange={handleDatesChange}
      onFocusChange={() => {
        // do nothing
      }}
      showClearDates
      small
      startDate={beginDate}
      startDateId={`date_range_start_date_${id}`}
      startDatePlaceholderText={formatMessage({
        defaultMessage: 'From',
        id: 'campaignPlans.filters.date.from'
      })}
    />
  );
};

DateRange.displayName = 'DateRange';

export default DateRange;
